import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type { ReceivedDocumentDetails } from "../types";

export interface UseReceivedDocumentResult {
  document: ReceivedDocumentDetails | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useReceivedDocument(
  documentId: string,
): UseReceivedDocumentResult {
  const [document, setDocument] = useState<ReceivedDocumentDetails | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocument = useCallback(async () => {
    if (!documentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch.get<ReceivedDocumentDetails>(
        `/api/received-documents/${documentId}`,
      );
      setDocument(response);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch document"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  return {
    document,
    isLoading,
    error,
    refetch: fetchDocument,
  };
}
